/**
 * TimelineView - Visual timeline with events and life phases
 */

import * as React from 'react'
import { motion } from 'framer-motion'
import { useAppSelector } from '@/store/hooks'
import type { LifeEvent, LifePhase } from '@/types'
import { CATEGORY_COLORS, EVENT_ICONS } from '@/types/timeline.types'
import * as LucideIcons from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { Plus, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Calendar } from 'lucide-react'

interface TimelineViewProps {
  events: LifeEvent[]
  phases: LifePhase[]
  viewStartYear: number
  viewEndYear: number
  selectedEventId: string | null
  onEventClick?: (event: LifeEvent) => void
  onAddEvent?: () => void
  onYearRangeChange?: (startYear: number, endYear: number) => void
  className?: string
}

export function TimelineView({
  events,
  phases,
  viewStartYear,
  viewEndYear,
  selectedEventId,
  onEventClick,
  onAddEvent,
  onYearRangeChange,
  className,
}: TimelineViewProps) {
  const lifePlan = useAppSelector((state) => state.lifePlan)
  
  // Calculate current year and age
  const currentYear = new Date().getFullYear()
  const birthYear = lifePlan.personal.birthDate 
    ? new Date(lifePlan.personal.birthDate).getFullYear() 
    : currentYear - 30
  
  // Year range for display
  const years = React.useMemo(() => {
    const result: number[] = []
    for (let year = viewStartYear; year <= viewEndYear; year++) {
      result.push(year)
    }
    return result
  }, [viewStartYear, viewEndYear])
  
  // Group events by year
  const eventsByYear = React.useMemo(() => {
    const map = new Map<number, LifeEvent[]>()
    events.forEach(event => {
      const eventYear = new Date(event.date).getFullYear()
      if (!map.has(eventYear)) {
        map.set(eventYear, [])
      }
      map.get(eventYear)!.push(event)
    })
    return map
  }, [events])
  
  // Get phase for a given year
  const getPhaseForYear = (year: number): LifePhase | undefined => {
    return phases.find(phase => {
      const startYear = new Date(phase.startDate).getFullYear()
      const endYear = new Date(phase.endDate).getFullYear()
      return year >= startYear && year <= endYear
    })
  }
  
  // Navigation
  const handlePrevDecade = () => {
    onYearRangeChange?.(viewStartYear - 10, viewEndYear - 10)
  }
  
  const handleNextDecade = () => {
    onYearRangeChange?.(viewStartYear + 10, viewEndYear + 10)
  }
  
  const handleZoomIn = () => {
    const midYear = Math.floor((viewStartYear + viewEndYear) / 2)
    const newRange = Math.max(10, Math.floor((viewEndYear - viewStartYear) / 2))
    onYearRangeChange?.(midYear - Math.floor(newRange / 2), midYear + Math.ceil(newRange / 2))
  }
  
  const handleZoomOut = () => {
    const midYear = Math.floor((viewStartYear + viewEndYear) / 2)
    const newRange = Math.min(80, (viewEndYear - viewStartYear) * 2)
    onYearRangeChange?.(midYear - Math.floor(newRange / 2), midYear + Math.ceil(newRange / 2))
  }
  
  // Get icon component
  const getIcon = (iconName: string) => {
    return LucideIcons[iconName as keyof typeof LucideIcons] as React.ComponentType<{ className?: string }> | undefined
  }
  
  return (
    <div className={cn('space-y-4', className)}>
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handlePrevDecade}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium min-w-[120px] text-center">
            {viewStartYear} - {viewEndYear}
          </span>
          <Button variant="outline" size="icon" onClick={handleNextDecade}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handleZoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={handleZoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          {onAddEvent && (
            <Button onClick={onAddEvent}>
              <Plus className="mr-2 h-4 w-4" />
              Add Event
            </Button>
          )}
        </div>
      </div>
      
      {/* Timeline */}
      <Card>
        <CardContent className="p-4 overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Life phases bar */}
            <div className="flex h-8 mb-4 rounded-lg overflow-hidden">
              {years.map((year, index) => {
                const phase = getPhaseForYear(year)
                const isFirstOfPhase = index === 0 || getPhaseForYear(years[index - 1])?.id !== phase?.id
                
                return (
                  <Tooltip key={year}>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          'flex-1 flex items-center justify-center text-xs font-medium',
                          'transition-colors cursor-pointer hover:opacity-80',
                          year === currentYear && 'ring-2 ring-primary ring-inset'
                        )}
                        style={{ backgroundColor: phase?.color || '#64748b20' }}
                      >
                        {isFirstOfPhase && phase && (
                          <span className="text-white text-[10px] truncate px-1">
                            {phase.name}
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="text-xs">
                        <div className="font-medium">{phase?.name || 'No phase'}</div>
                        <div className="text-muted-foreground">Age {year - birthYear}</div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </div>
            
            {/* Year labels */}
            <div className="flex mb-2 border-b pb-2">
              {years.map((year) => (
                <div
                  key={year}
                  className={cn(
                    'flex-1 text-center text-xs',
                    year === currentYear && 'font-bold text-primary',
                    year % 5 === 0 ? 'opacity-100' : 'opacity-50'
                  )}
                >
                  {year % 5 === 0 ? year : '·'}
                </div>
              ))}
            </div>
            
            {/* Age labels */}
            <div className="flex mb-4 text-muted-foreground">
              {years.map((year) => (
                <div
                  key={year}
                  className={cn(
                    'flex-1 text-center text-[10px]',
                    year === currentYear && 'font-bold text-primary'
                  )}
                >
                  {year % 5 === 0 ? `Age ${year - birthYear}` : ''}
                </div>
              ))}
            </div>
            
            {/* Events row */}
            <div className="flex min-h-[80px] relative">
              {years.map((year) => {
                const yearEvents = eventsByYear.get(year) || []
                
                return (
                  <div
                    key={year}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1 pt-2',
                      'border-l first:border-l-0 border-dashed border-border/50',
                      year === currentYear && 'bg-primary/5'
                    )}
                  >
                    {/* Current year indicator */}
                    {year === currentYear && (
                      <div className="absolute top-0 w-0.5 h-full bg-primary/30" />
                    )}
                    
                    {/* Events */}
                    {yearEvents.map((event) => {
                      const IconComponent = getIcon(EVENT_ICONS[event.type])
                      const categoryColor = CATEGORY_COLORS[event.category]
                      
                      return (
                        <Tooltip key={event.id}>
                          <TooltipTrigger asChild>
                            <motion.button
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              whileHover={{ scale: 1.1 }}
                              className={cn(
                                'w-8 h-8 rounded-full flex items-center justify-center',
                                'transition-all cursor-pointer',
                                'ring-2 ring-offset-2 ring-offset-background',
                                selectedEventId === event.id && 'ring-4',
                                event.confidence === 'possible' && 'opacity-60'
                              )}
                              style={{ backgroundColor: categoryColor }}
                              onClick={() => onEventClick?.(event)}
                            >
                              {IconComponent && (
                                <IconComponent className="h-4 w-4 text-white" />
                              )}
                            </motion.button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[200px]">
                            <div className="space-y-1">
                              <div className="font-medium">{event.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {new Date(event.date).toLocaleDateString()}
                              </div>
                              {event.financialImpact?.oneTimeIncome && (
                                <div className="text-xs text-green-500">
                                  +${event.financialImpact.oneTimeIncome.toLocaleString()} income
                                </div>
                              )}
                              {event.financialImpact?.oneTimeExpense && (
                                <div className="text-xs text-red-500">
                                  -${event.financialImpact.oneTimeExpense.toLocaleString()} expense
                                </div>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Events list */}
      {events.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Upcoming Events
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {events
              .filter(e => new Date(e.date) >= new Date())
              .slice(0, 5)
              .map((event) => {
                const IconComponent = getIcon(EVENT_ICONS[event.type])
                const categoryColor = CATEGORY_COLORS[event.category]
                
                return (
                  <button
                    key={event.id}
                    className={cn(
                      'w-full flex items-center gap-3 p-3 rounded-lg',
                      'hover:bg-muted/50 transition-colors text-left',
                      selectedEventId === event.id && 'bg-muted'
                    )}
                    onClick={() => onEventClick?.(event)}
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: categoryColor }}
                    >
                      {IconComponent && (
                        <IconComponent className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{event.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(event.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                    <Badge variant="outline" className="capitalize">
                      {event.confidence}
                    </Badge>
                  </button>
                )
              })}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
